import type useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import type { ForecastWeatherResponse } from '/@/types/weather/forecast';
// Reuse the existing suitability logic (G/Y/R counting) rather than duplicating it.
import { getSuggestion } from '/@/views/fields/field-detail/field-weather/application-risk';
import type { ApplicationRiskShape, ForecastRow } from './types';

type WeatherData = ForecastWeatherResponse['data'];
type UnitProvider = ReturnType<typeof useUnitProvider>;

export type PillVariant = 'apto' | 'apto-adjust' | 'no-apto';

const EMPTY_RISK: ApplicationRiskShape = { drift: '', wash: '', evaporation: '' };

const pad = (n: number) => n.toString().padStart(2, '0');

/**
 * Maps an application risk to one of the three visual pill states.
 * Delegates to the shared `getSuggestion` so the thresholds stay in one place.
 */
export const getPillVariant = (risk: ApplicationRiskShape): PillVariant => {
    const suggestion = getSuggestion(risk) as { color: string; label: string; disclaimer?: string };
    if (suggestion.disclaimer) return 'apto-adjust';
    if (suggestion.color === 'G') return 'apto';
    return 'no-apto';
};

const isSameDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** Builds the "Próximas horas" rows, limited to the hours falling on `now`'s calendar day. */
export const buildHourlyRows = (data: WeatherData, units: UnitProvider, now: Date = new Date()): ForecastRow[] => {
    const entries = data.hourlyForecasts ?? [];
    const hours = entries.map((e) => new Date(e.date).getHours());
    // Derive the range width from the full-series cadence, defaulting to 3h.
    const first = hours[0];
    const second = hours[1];
    const interval = first !== undefined && second !== undefined ? (((second - first) % 24) + 24) % 24 || 3 : 3;

    return entries
        .filter((entry) => isSameDay(new Date(entry.date), now))
        .map((entry) => {
            const start = new Date(entry.date).getHours();
            const end = (start + interval) % 24;
            return {
                id: entry.date,
                label: `${pad(start)} ${_('a')} ${pad(end)}`,
                temp: units.toTemperature(entry.data.temp, 0).toString(),
                rain: `${Math.round(entry.data.rain)}%`,
                humidity: `${Math.round(entry.data.humidity)}%`,
                wind: units.toSpeed(entry.data.windSpeed, 0).toString(),
                risk: entry.data.applicationRisk,
            };
        });
};

const formatDayLabel = (dateStr: string): string => {
    // Daily dates are date-only ("YYYY-MM-DD"); anchor to local midnight to avoid tz shifts.
    const date = new Date(`${dateStr}T00:00:00`);
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`;
};

/** Builds the "Extendido" rows from the daily forecast, using daytime values. */
export const buildDailyRows = (data: WeatherData, units: UnitProvider): ForecastRow[] => {
    return (data.forecast ?? []).map((forecast) => {
        // Mirror WeatherForecastMiniCard: use the first provider's daytime forecast.
        const day = forecast.data?.[0]?.day;
        return {
            id: forecast.date,
            label: formatDayLabel(forecast.date),
            temp: day ? units.toTemperature(day.temperature, 0).toString() : '-',
            rain: day ? `${Math.round(day.rain)}%` : '-',
            humidity: day ? `${Math.round(day.humidity)}%` : '-',
            wind: day ? units.toSpeed(day.windSpeed, 0).toString() : '-',
            risk: day?.applicationRisk ?? EMPTY_RISK,
        };
    });
};

/** Whether the "próx. 7 días" frost-risk banner should be shown. */
export const hasFrostRisk = (data: WeatherData): boolean => {
    const risk = data.lowGDUsRisk?.['7d'];
    return risk === 'R' || risk === 'O' || risk === 'Y';
};
